/**
 * Pricing Service
 *
 * Calculates tiered pricing based on participant count.
 * Supports slot price_override and price_multiplier (applied to tier price).
 * Whole group pays the same per-person rate for their tier.
 */

import type { Activity, PriceTier } from '@/types';
import type { VenueAvailabilitySlot } from '@/types/scraper';

/**
 * Parse price_tiers from database (JSONB)
 */
export function parsePriceTiers(raw: unknown): PriceTier[] {
  if (!raw || !Array.isArray(raw)) return [];
  return (raw as Array<{ maxParticipants?: number; pricePerPerson?: number }>)
    .map((t) => ({
      maxParticipants: Number(t.maxParticipants) || 0,
      pricePerPerson: Number(t.pricePerPerson) || 0,
    }))
    .filter((t) => t.maxParticipants > 0 && t.pricePerPerson > 0)
    .sort((a, b) => a.maxParticipants - b.maxParticipants);
}

/**
 * Get lowest price from tiers (for "À partir de" display and sorting)
 */
export function getLowestPriceFromTiers(priceTiers: PriceTier[]): number {
  if (!priceTiers?.length) return 0;
  const sorted = [...priceTiers].sort((a, b) => a.pricePerPerson - b.pricePerPerson);
  return sorted[0].pricePerPerson;
}

/**
 * Get base price per person from tiers for given participant count
 */
function getPriceFromTiers(tiers: PriceTier[], participants: number): number {
  if (!tiers?.length) return 0;
  const sorted = [...tiers].sort((a, b) => a.maxParticipants - b.maxParticipants);
  const tier = sorted.find((t) => participants <= t.maxParticipants);
  return tier ? tier.pricePerPerson : sorted[sorted.length - 1].pricePerPerson;
}

/**
 * Apply legacy slot pricing (override or multiplier) to a base per-person price
 */
function applyLegacySlotPricing(
  basePricePerPerson: number,
  slot?: VenueAvailabilitySlot
): number {
  if (!slot) return basePricePerPerson;

  if (slot.priceOverride !== undefined && slot.priceOverride !== null) {
    if (slot.priceOverride <= 0) return basePricePerPerson;
    return slot.priceOverride;
  }

  if (slot.priceMultiplier !== undefined && slot.priceMultiplier !== null && slot.priceMultiplier > 0) {
    return basePricePerPerson * slot.priceMultiplier;
  }

  return basePricePerPerson;
}

/**
 * Get price per person for given participant count (tier + slot)
 * Priority: slot.priceTiers > slot.priceOverride > slot.priceMultiplier × activity tiers > activity tiers
 */
export function getPricePerPersonForParticipants(
  activity: Activity,
  participants: number,
  slot?: VenueAvailabilitySlot
): number {
  try {
    // Use slot tiers when set (e.g. heures pleines), else activity tiers
    const tiersToUse = slot?.priceTiers?.length ? slot.priceTiers : activity.priceTiers;
    const base = getPriceFromTiers(tiersToUse, participants);

    // Apply legacy override/multiplier only when slot has no price_tiers
    if (slot?.priceTiers?.length) {
      return base; // Slot has its own tiers, no override/multiplier
    }
    return applyLegacySlotPricing(base, slot);
  } catch (error) {
    console.error('Error getting price per person:', error);
    return getLowestPriceFromTiers(activity.priceTiers);
  }
}

/**
 * Calculate price per person for a slot (used when participant count unknown, e.g. display)
 * Uses 1 participant for tier lookup.
 */
export function calculateSlotPrice(
  activity: Activity,
  slot?: VenueAvailabilitySlot
): number {
  return getPricePerPersonForParticipants(activity, 1, slot);
}

/**
 * Calculate total booking price
 * total = (price per person for this group size) × participants × numberOfGames
 */
export function calculateBookingPrice(
  activity: Activity,
  slot: VenueAvailabilitySlot | undefined,
  participants: number,
  numberOfGames: number = 1
): number {
  try {
    const pricePerPerson = getPricePerPersonForParticipants(activity, participants, slot);
    return pricePerPerson * participants * numberOfGames;
  } catch (error) {
    console.error('Error calculating booking price:', error);
    const fallback = getLowestPriceFromTiers(activity.priceTiers);
    return fallback * participants * numberOfGames;
  }
}

/**
 * Check if slot has custom pricing (different from base tier price)
 */
export function hasCustomPricing(
  activity: Activity,
  slot?: VenueAvailabilitySlot
): boolean {
  if (!slot) return false;
  const basePrice = getPriceFromTiers(activity.priceTiers, 1);
  const slotPrice = calculateSlotPrice(activity, slot);
  return slotPrice !== basePrice;
}

/**
 * Get pricing label for display (slot overrides)
 */
export function getPricingLabel(
  activity: Activity,
  slot?: VenueAvailabilitySlot
): string | null {
  if (!slot) return null;

  if (slot.priceTiers?.length) {
    return 'Tarif heures pleines';
  }

  if (slot.priceOverride !== undefined && slot.priceOverride !== null) {
    return `Prix spécial: ${slot.priceOverride.toFixed(2)}€`;
  }

  if (slot.priceMultiplier !== undefined && slot.priceMultiplier !== null && slot.priceMultiplier > 1.0) {
    const percentage = Math.round((slot.priceMultiplier - 1) * 100);
    return `Tarif premium (+${percentage}%)`;
  }

  return null;
}
